import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const AdvancedCanvas = ({ elements, onElementUpdate, template, aiContent, isVisible }) => {
  const canvasRef = useRef(null);
  const [selectedElement, setSelectedElement] = useState(null);
  const [dragState, setDragState] = useState(null);
  const [canvasScale, setCanvasScale] = useState(1);
  const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 });
  const [showGrid, setShowGrid] = useState(true);
  const [snapToGrid, setSnapToGrid] = useState(true);

  // Grid size for snapping
  const GRID_SIZE = 20;

  // Snap coordinates to grid if enabled
  const snapCoordinates = (x, y) => {
    if (!snapToGrid) return { x, y };
    return {
      x: Math.round(x / GRID_SIZE) * GRID_SIZE,
      y: Math.round(y / GRID_SIZE) * GRID_SIZE
    };
  };

  // Handle element selection
  const handleElementClick = (element, event) => {
    event?.stopPropagation();
    setSelectedElement(element);
  };

  // Handle element dragging
  const handleMouseDown = (element, event) => {
    event?.preventDefault();
    const rect = canvasRef?.current?.getBoundingClientRect();
    const startX = (event?.clientX - rect?.left) / canvasScale - canvasOffset?.x;
    const startY = (event?.clientY - rect?.top) / canvasScale - canvasOffset?.y;
    
    setDragState({
      element,
      startX: startX - element?.position?.x,
      startY: startY - element?.position?.y,
      isDragging: true
    });
  };

  // Handle mouse move for dragging
  const handleMouseMove = (event) => {
    if (!dragState?.isDragging) return;

    const rect = canvasRef?.current?.getBoundingClientRect();
    const currentX = (event?.clientX - rect?.left) / canvasScale - canvasOffset?.x;
    const currentY = (event?.clientY - rect?.top) / canvasScale - canvasOffset?.y;

    const newX = currentX - dragState?.startX;
    const newY = currentY - dragState?.startY;
    
    const snappedCoords = snapCoordinates(newX, newY);

    const updatedElements = elements?.map(el =>
      el?.id === dragState?.element?.id
        ? { ...el, position: snappedCoords }
        : el
    );

    onElementUpdate?.(updatedElements);
  };

  // Handle mouse up to end dragging
  const handleMouseUp = () => {
    setDragState(null);
  };

  // Handle canvas click (deselect elements)
  const handleCanvasClick = () => {
    setSelectedElement(null);
  };

  // Handle element deletion
  const handleDeleteElement = (elementId) => {
    const updatedElements = elements?.filter(el => el?.id !== elementId);
    onElementUpdate?.(updatedElements);
    setSelectedElement(null);
  };

  // Handle element duplication
  const handleDuplicateElement = (element) => {
    const newElement = {
      ...element,
      id: `${element?.id}-copy-${Date.now()}`,
      position: {
        x: element?.position?.x + 20,
        y: element?.position?.y + 20
      }
    };
    onElementUpdate?.([...elements, newElement]);
  };

  // Handle element resizing
  const handleResizeElement = (elementId, newSize) => {
    const updatedElements = elements?.map(el =>
      el?.id === elementId
        ? { ...el, size: newSize }
        : el
    );
    onElementUpdate?.(updatedElements);
  };

  // Canvas zoom controls
  const handleZoomIn = () => {
    setCanvasScale(prev => Math.min(prev + 0.1, 2));
  };

  const handleZoomOut = () => {
    setCanvasScale(prev => Math.max(prev - 0.1, 0.25));
  };

  const handleZoomReset = () => {
    setCanvasScale(1);
    setCanvasOffset({ x: 0, y: 0 });
  };

  // Render element based on type
  const renderElement = (element) => {
    const isSelected = selectedElement?.id === element?.id;
    const style = {
      position: 'absolute',
      left: element?.position?.x,
      top: element?.position?.y,
      width: element?.size?.width,
      height: element?.size?.height,
      cursor: 'move',
      border: isSelected ? '2px solid #3b82f6' : '1px solid #e2e8f0',
      borderRadius: element?.style?.borderRadius || 8,
      backgroundColor: element?.style?.backgroundColor || '#ffffff',
      padding: element?.style?.padding || 16,
      boxShadow: isSelected ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : element?.style?.boxShadow,
      overflow: 'hidden',
      ...element?.style
    };

    const commonProps = {
      key: element?.id,
      style,
      onClick: (e) => handleElementClick(element, e),
      onMouseDown: (e) => handleMouseDown(element, e)
    };

    switch (element?.type) {
      case 'author-bio':
        return (
          <div {...commonProps}>
            <div className="flex space-x-3">
              {element?.content?.image && (
                <img
                  src={element?.content?.image}
                  alt={element?.content?.imageAlt || 'Author photo showing professional headshot for press kit'}
                  className="w-16 h-16 rounded-full object-cover border-2 border-slate-200"
                />
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-slate-900 text-sm mb-1">
                  {element?.content?.name || 'Author Name'}
                </h3>
                <p className="text-xs text-slate-600 line-clamp-4">
                  {element?.content?.bio || 'Author biography will appear here...'}
                </p>
              </div>
            </div>
          </div>
        );

      case 'book-cover':
        return (
          <div {...commonProps} className="flex flex-col items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
            {element?.content?.image ? (
              <img
                src={element?.content?.image}
                alt={element?.content?.imageAlt || 'Book cover showing title and author name with professional design'}
                className="w-full h-full object-cover rounded-lg"
              />
            ) : (
              <div className="text-center">
                <Icon name="BookOpen" size={32} className="text-slate-400 mx-auto mb-2" />
                <p className="text-xs text-slate-500">Book Cover</p>
              </div>
            )}
          </div>
        );

      case 'press-release':
        return (
          <div {...commonProps}>
            <div className="space-y-2">
              <div className="flex items-center space-x-2 mb-3">
                <Icon name="FileText" size={16} className="text-indigo-600" />
                <span className="text-xs font-medium text-indigo-600 uppercase tracking-wide">Press Release</span>
              </div>
              <h3 className="font-bold text-slate-900 text-sm line-clamp-2">
                {element?.content?.headline || 'Press Release Headline'}
              </h3>
              {element?.content?.subheadline && (
                <p className="text-xs text-slate-600 line-clamp-1">
                  {element?.content?.subheadline}
                </p>
              )}
              <p className="text-xs text-slate-500 line-clamp-3">
                {element?.content?.body || 'Press release content will appear here...'}
              </p>
            </div>
          </div>
        );

      case 'social-media-feed':
        return (
          <div {...commonProps}>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Icon name="Share2" size={16} className="text-blue-500" />
                <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">
                  {element?.content?.platform || 'Social Media'}
                </span>
              </div>
              {element?.content?.posts?.map((post, index) => (
                <div key={index} className="p-2 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-700 mb-1">{post?.text}</p>
                  {post?.hashtags?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {post?.hashtags?.slice(0, 3)?.map((hashtag, i) => (
                        <span key={i} className="text-xs text-blue-600">#{hashtag}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      case 'media-gallery':
        return (
          <div {...commonProps}>
            <div className="h-full flex flex-col">
              <div className="flex items-center space-x-2 mb-3">
                <Icon name="Images" size={16} className="text-purple-600" />
                <span className="text-xs font-medium text-purple-600 uppercase tracking-wide">Media Gallery</span>
              </div>
              <div className="flex-1 grid grid-cols-2 gap-2">
                {[1, 2, 3, 4]?.map((i) => (
                  <div key={i} className="aspect-square bg-slate-200 rounded flex items-center justify-center">
                    <Icon name="Image" size={16} className="text-slate-400" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div {...commonProps}>
            <div className="h-full flex items-center justify-center text-slate-400">
              <Icon name="Square" size={24} />
            </div>
          </div>
        );
    }
  };

  // Add event listeners for dragging
  useEffect(() => {
    const canvas = canvasRef?.current;
    if (!canvas) return;

    canvas?.addEventListener('mousemove', handleMouseMove);
    canvas?.addEventListener('mouseup', handleMouseUp);
    canvas?.addEventListener('mouseleave', handleMouseUp);

    return () => {
      canvas?.removeEventListener('mousemove', handleMouseMove);
      canvas?.removeEventListener('mouseup', handleMouseUp);
      canvas?.removeEventListener('mouseleave', handleMouseUp);
    };
  }, [dragState]);

  if (!isVisible) return null;

  return (
    <div className="flex-1 bg-slate-50 border-r border-slate-200 flex flex-col">
      {/* Canvas Toolbar */}
      <div className="flex items-center justify-between p-3 bg-white border-b border-slate-200">
        <div className="flex items-center space-x-4">
          <h3 className="text-sm font-semibold text-slate-900">Design Canvas</h3>
          
          {/* Grid Controls */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowGrid(!showGrid)}
              className={`p-2 rounded-lg transition-colors ${
                showGrid ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-500'
              }`}
            >
              <Icon name="Grid3X3" size={14} />
            </button>
            <button
              onClick={() => setSnapToGrid(!snapToGrid)}
              className={`p-2 rounded-lg transition-colors ${
                snapToGrid ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-500'
              }`}
            >
              <Icon name="Magnet" size={14} />
            </button>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {/* Zoom Controls */}
          <div className="flex items-center space-x-1 bg-slate-100 rounded-lg p-1">
            <button onClick={handleZoomOut} className="p-1 hover:bg-slate-200 rounded">
              <Icon name="ZoomOut" size={14} className="text-slate-600" />
            </button>
            <span className="px-2 text-xs font-medium text-slate-700 min-w-[3rem] text-center">
              {Math.round(canvasScale * 100)}%
            </span>
            <button onClick={handleZoomIn} className="p-1 hover:bg-slate-200 rounded">
              <Icon name="ZoomIn" size={14} className="text-slate-600" />
            </button>
            <button onClick={handleZoomReset} className="p-1 hover:bg-slate-200 rounded">
              <Icon name="RotateCcw" size={14} className="text-slate-600" />
            </button>
          </div>

          {/* Element Count */}
          <div className="text-xs text-slate-500">
            {elements?.length || 0} elements
          </div>
        </div>
      </div>
      {/* Canvas Area */}
      <div className="flex-1 overflow-hidden relative">
        <div
          ref={canvasRef}
          className="w-full h-full overflow-auto"
          onClick={handleCanvasClick}
          style={{
            backgroundImage: showGrid ? 
              `radial-gradient(circle, #e2e8f0 1px, transparent 1px)` : 'none',
            backgroundSize: showGrid ? `${GRID_SIZE}px ${GRID_SIZE}px` : 'auto',
            cursor: dragState?.isDragging ? 'grabbing' : 'default'
          }}
        >
          <div
            className="relative min-w-full min-h-full"
            style={{
              transform: `scale(${canvasScale}) translate(${canvasOffset?.x}px, ${canvasOffset?.y}px)`,
              transformOrigin: 'top left',
              width: '1200px',
              height: '800px'
            }}
          >
            {/* Template Background */}
            {template && (
              <div className="absolute inset-0 opacity-10 pointer-events-none">
                <img
                  src={template?.preview}
                  alt="Template background overlay for design reference"
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Render Elements */}
            {elements?.map(renderElement)}

            {/* Selection Handles */}
            {selectedElement && (
              <div className="absolute pointer-events-none">
                <div
                  className="absolute border-2 border-indigo-500 bg-indigo-500/10"
                  style={{
                    left: selectedElement?.position?.x - 2,
                    top: selectedElement?.position?.y - 2,
                    width: selectedElement?.size?.width + 4,
                    height: selectedElement?.size?.height + 4,
                    borderRadius: 8
                  }}
                />
                
                {/* Resize Handles */}
                {[
                  { pos: 'top-left', x: -4, y: -4 },
                  { pos: 'top-right', x: selectedElement?.size?.width, y: -4 },
                  { pos: 'bottom-left', x: -4, y: selectedElement?.size?.height },
                  { pos: 'bottom-right', x: selectedElement?.size?.width, y: selectedElement?.size?.height }
                ]?.map(({ pos, x, y }) => (
                  <div
                    key={pos}
                    className="absolute w-2 h-2 bg-indigo-500 border border-white rounded-sm cursor-nw-resize pointer-events-auto"
                    style={{
                      left: selectedElement?.position?.x + x,
                      top: selectedElement?.position?.y + y
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Element Actions Toolbar */}
        {selectedElement && (
          <div className="absolute top-4 right-4 flex items-center space-x-1 bg-white border border-slate-200 rounded-lg shadow-lg p-1">
            <button
              onClick={() => handleDuplicateElement(selectedElement)}
              className="p-2 hover:bg-slate-100 rounded text-slate-600"
              title="Duplicate Element"
            >
              <Icon name="Copy" size={14} />
            </button>
            <button
              className="p-2 hover:bg-slate-100 rounded text-slate-600"
              title="Edit Element"
            >
              <Icon name="Edit2" size={14} />
            </button>
            <button
              onClick={() => handleDeleteElement(selectedElement?.id)}
              className="p-2 hover:bg-red-100 rounded text-red-600"
              title="Delete Element"
            >
              <Icon name="Trash2" size={14} />
            </button>
          </div>
        )}
      </div>
      {/* Canvas Status */}
      <div className="px-4 py-2 bg-slate-100 border-t border-slate-200 text-xs text-slate-600">
        <div className="flex items-center justify-between">
          <span>Canvas: 1200×800px • Scale: {Math.round(canvasScale * 100)}%</span>
          {selectedElement && (
            <span>
              Selected: {selectedElement?.type} • 
              Position: {selectedElement?.position?.x}, {selectedElement?.position?.y} • 
              Size: {selectedElement?.size?.width}×{selectedElement?.size?.height}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdvancedCanvas;